import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { postSystemLogin, fetchLoginCaptcha, AUTH_TOKEN_KEY, ApiRequestError } from '@/api';

interface User {
  email: string;
  name: string;
  sysname: string;
  role?: string;
  isTeamAdmin?: boolean;
  avatarUrl?: string;
  mobile?: string;
  user_id?: string;
  gender?: number;
  postalCode?: string;
  postalCodes?: string[];
}

export interface LoginCaptchaPayload {
  captchaImageId: string;
  captchaCode: string;
}

export interface EstablishSessionProfile {
  email: string;
  name: string;
  sysname?: string;
  avatarUrl?: string;
  role?: string;
  mobile?: string;
  user_id?: string;
  gender?: number;
  postalCode?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
    captcha?: LoginCaptchaPayload
  ) => Promise<{ success: boolean; error?: string; message?: string }>;
  /** Persist token and user profile after registration (or other server-issued sessions). */
  establishSession: (token: string, profile: EstablishSessionProfile) => void;
  logout: () => void;
  updateUser?: (data: Partial<User>) => void;
  /** Re-apply the last persisted user snapshot from `localStorage` (e.g. when remote profile fetch fails). */
  hydrateUserFromStorage: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY = 'greenflow_auth_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  const login = async (
    email: string,
    password: string,
    captcha?: LoginCaptchaPayload
  ): Promise<{ success: boolean; error?: string; message?: string }> => {
    const userName = email.trim();
    if (!userName) {
      return { success: false, error: 'Please enter your email address.' };
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userName)) {
      return { success: false, error: 'Please enter a valid email address.' };
    }
    if (password.length < 6) {
      return { success: false, error: 'Password must be at least 6 characters.' };
    }
    if (captcha && (!captcha.captchaImageId.trim() || !captcha.captchaCode.trim())) {
      return { success: false, error: 'Please complete the captcha.' };
    }

    if (captcha) {
      try {
        const res = await postSystemLogin({
          userName,
          password,
          image_id: captcha.captchaImageId.trim(),
          check_num: captcha.captchaCode.trim(),
        });
        if (res.token) {
          localStorage.setItem(AUTH_TOKEN_KEY, res.token);
        } else {
          localStorage.removeItem(AUTH_TOKEN_KEY);
        }
        setUser({
          email: res.email || userName,
          name: res.userName || userName.split('@')[0],
          avatarUrl: res.avatar || undefined,
          role: res.role || undefined,
          mobile: res.mobile || undefined,
          user_id: res.user_id || undefined,
          sysname: res.userName?.trim() || userName.split('@')[0],
          gender: res.gender || undefined,
        });
        return { success: true, message: res.message };
      } catch (e) {
        void fetchLoginCaptcha().catch(() => undefined);
        const msg = e instanceof ApiRequestError ? e.message : 'Login failed.';
        return { success: false, error: msg };
      }
    }

    // No captcha (e.g. sign-in before captcha is required): keep lightweight local session.
    const mockName = userName
      .split('@')[0]
      .replace(/[._]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const mockUser: User = {
      email: userName,
      name: mockName,
      sysname: userName.split('@')[0],
    };
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setUser(mockUser);
    return { success: true };
  };

  const establishSession = useCallback((token: string, profile: EstablishSessionProfile) => {
    const t = token.trim();
    if (t) {
      localStorage.setItem(AUTH_TOKEN_KEY, t);
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
    setUser({
      email: profile.email,
      name: profile.name,
      sysname: profile.sysname ?? profile.name,
      avatarUrl: profile.avatarUrl,
      role: profile.role,
      mobile: profile.mobile,
      user_id: profile.user_id,
      gender: profile.gender,
    });
  }, []);

  const logout = () => {
    setUser(null);
    localStorage.removeItem(AUTH_TOKEN_KEY);
  };

  const updateUser = useCallback((data: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...data } : null));
  }, []);

  const hydrateUserFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      setUser(JSON.parse(stored) as User);
    } catch {
      /* ignore corrupt storage */
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, login, establishSession, logout, updateUser, hydrateUserFromStorage }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
